/**
 * Rechenzentren, Racks und Server.
 *
 * Die drei Ebenen hängen so eng zusammen (ein Server braucht ein Rack, ein Rack
 * einen Standort, beide ein Strombudget), dass sie sich eine Datei teilen –
 * inklusive der gemeinsamen Kaufdialoge.
 */
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import type { DeviceKindKey, DeviceState, OperatingSystem, PartKind, Server, ServerLoadRow, ServerRole } from '@hosting-sim/core';
import {
  PART_KIND_LABEL,
  activeModifiers,
  fiberLatencyMs,
  findPart,
  findServerModel,
  previewUpgrade,
  serverUtilization,
  slotUsage,
} from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  Column,
  DataTable,
  EmptyState,
  Field,
  Meter,
  Modal,
  Mono,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  StatusDot,
  Spinner,
  Tabs,
  type Tone,
} from '../components/ui';
import { SellDeviceButton, ReplacementHint } from '../components/SellDevice';
import { ContractPanel, NegotiationDialog, RelocateDeviceButton } from '../components/Colocation';
import { ServerConfiguratorDialog } from '../components/ServerConfigurator';
import { ConsolidateButton, RetirementNotice } from '../components/Consolidation';
import { EvacuateServerButton } from './Migration';
import { ServerConsole } from '../components/ServerConsole';
import { useActiveEvents, useGame, useMetrics, useServerLoad } from '../store/game';

/**
 * Die 3D-Rackansicht wird erst geladen, wenn sie jemand aufruft.
 *
 * three.js samt Umgebung wiegt ein Vielfaches der übrigen Seite. Fest
 * eingebunden verlängerte es den ersten Aufbau des Panels für alle – auch für
 * die, die nie in ein Rack hineinsehen. Als eigener Brocken kostet es nur den,
 * der den Reiter anklickt.
 */
const RackViewer = lazy(() => import('../components/rack3d/RackViewer'));
import { api } from '../lib/api';
import { COUNTRY_NAMES, bytes, dec, duration, money, num, watt } from '../lib/format';

// ---------------------------------------------------------------------------
// Gemeinsame Darstellungen
// ---------------------------------------------------------------------------

export const STATE_TONE: Record<DeviceState, Tone> = {
  online: 'success',
  booting: 'info',
  degraded: 'warning',
  failed: 'danger',
  offline: 'muted',
  maintenance: 'info',
};

export const STATE_LABEL: Record<DeviceState, string> = {
  online: 'Online',
  booting: 'Startet',
  degraded: 'Beeinträchtigt',
  failed: 'Defekt',
  offline: 'Aus',
  maintenance: 'Wartung',
};

const ROLE_LABEL: Record<ServerRole, string> = {
  'shared-hosting': 'Shared Hosting',
  'vps-host': 'VPS-Host',
  dedicated: 'Dedicated',
  storage: 'Storage',
  mail: 'Mail',
  dns: 'DNS',
  backup: 'Backup',
  'gpu-host': 'GPU-Host',
  unassigned: 'Nicht zugewiesen',
};

const OS_LABEL: Record<OperatingSystem, string> = {
  'linux-debian': 'Debian',
  'linux-ubuntu': 'Ubuntu',
  'linux-rocky': 'Rocky Linux',
  'windows-server': 'Windows Server',
  freebsd: 'FreeBSD',
};

/**
 * Zustandsanzeige samt Temperatur und Restzustand – überall gleich.
 *
 * „Aus“ und „Reserve“ sehen im Zustand gleich aus und meinen etwas anderes:
 * Das eine ist ein Zustand, den jemand erklären muss, das andere einer, den
 * jemand hergestellt hat. Wer das nicht unterscheidet, sucht in der Serverliste
 * nach einem Fehler, den es nicht gibt.
 */
export function DeviceStatus({
  device,
}: {
  device: { state: DeviceState; temperatureC: number; health: number; pendingTicks: number; standby?: boolean };
}) {
  const reserve = device.standby && device.state === 'offline';
  return (
    // inline-flex statt flex: Als Blockelement füllte die Zeile die ganze Zelle
    // und ignorierte deren Ausrichtung – die Werte standen dann links, obwohl
    // die Spalte rechtsbündig ist.
    <div className="inline-flex items-center gap-2 align-middle">
      <StatusDot
        tone={reserve ? 'info' : STATE_TONE[device.state]}
        label={reserve ? 'Reserve' : STATE_LABEL[device.state]}
        pulse={device.state === 'booting' || device.state === 'maintenance'}
      />
      {device.pendingTicks > 0 && <span className="text-2xs text-panel-500">noch {duration(device.pendingTicks)}</span>}
    </div>
  );
}

export const RESOURCE_LABEL: Record<ServerLoadRow['bottleneck'], string> = {
  cpu: 'CPU',
  ram: 'RAM',
  ssd: 'SSD',
  hdd: 'HDD',
};

/**
 * Belegung eines Servers: wieviel von dem, was er anbieten kann, vergeben ist.
 *
 * Gemessen wird gegen die Verkaufsgrenze, nicht gegen das Verbaute. Bei Shared
 * Hosting liegt die um ein Vielfaches darüber – ein Server mit 64 GB, auf dem
 * 200 GB zugesagt sind, ist nicht dreifach überlastet, sondern planmäßig
 * überbucht. Angezeigt wird die Ressource, die zuerst ausgeht; sie ist die
 * einzige, die über den nächsten Kunden entscheidet.
 */
export function FillCell({ row }: { row: ServerLoadRow | undefined }) {
  if (!row) return <span className="text-2xs text-panel-400">–</span>;
  return (
    <Meter
      value={row.fill}
      max={1}
      label={
        <>
          <span>{RESOURCE_LABEL[row.bottleneck]}</span>
          <span className="tabular">{Math.round(row.fill * 100)} %</span>
        </>
      }
    />
  );
}

/**
 * Momentane Rechenlast – was der Server gerade tatsächlich tut.
 *
 * Bewusst getrennt von der Belegung: Ein voll verkaufter Shared-Host kann im
 * Leerlauf stehen, und ein halbleerer VPS-Host kann am Anschlag laufen. Wer nur
 * eine der beiden Zahlen sieht, zieht regelmäßig den falschen Schluss.
 */
export function LoadCell({ row }: { row: ServerLoadRow | undefined }) {
  if (!row) return <span className="text-2xs text-panel-400">–</span>;
  return (
    <Meter
      value={row.load}
      max={1}
      label={
        <>
          <span className="tabular">{watt(row.watt)}</span>
          <span className="tabular">{Math.round(row.load * 100)} %</span>
        </>
      }
    />
  );
}

export function HealthCell({ health, temperatureC }: { health: number; temperatureC: number }) {
  return (
    <div className="inline-flex items-center gap-2 align-middle">
      <span className={`tabular text-xs ${health < 0.4 ? 'text-red-600 dark:text-red-400' : ''}`}>{Math.round(health * 100)} %</span>
      <span className={`tabular text-2xs ${temperatureC > 45 ? 'text-amber-600 dark:text-amber-400' : 'text-panel-400'}`}>
        {Math.round(temperatureC)} °C
      </span>
    </div>
  );
}

/** Lädt den Hardwarekatalog der laufenden Partie (abhängig vom Forschungsstand). */
export function useCatalog() {
  const gameId = useGame((s) => s.gameId);
  const [catalog, setCatalog] = useState<Awaited<ReturnType<typeof api.catalog>> | null>(null);

  useEffect(() => {
    if (!gameId) return;
    void api.catalog(gameId).then(setCatalog).catch(() => setCatalog(null));
  }, [gameId]);

  return catalog;
}

// ---------------------------------------------------------------------------
// Rechenzentren
// ---------------------------------------------------------------------------

/** Der Ausschnitt des Standortkatalogs, den der Eröffnungsdialog braucht. */
interface CatalogLocation {
  key: string;
  city: string;
  country: string;
  position: { x: number; y: number };
  setupCost: number;
  monthlyRent: number;
  powerPricePerKwh: number;
  ambientTempC: number;
  description: string;
  marketPotential: number;
}

interface LocationGroup {
  country: string;
  locations: { location: CatalogLocation; latencyMs: number | null }[];
}

/**
 * Gruppiert freie Standorte nach Land und berechnet je Standort die
 * Glasfaserlatenz zum nächstgelegenen eigenen Rechenzentrum.
 *
 * Sortiert wird nach dieser Latenz – innerhalb der Gruppe und zwischen den
 * Gruppen. Das Ergebnis ist eine Liste, die beim Naheliegenden beginnt: Wer
 * sein erstes Rechenzentrum in Frankfurt hat, sieht Köln vor Phoenix.
 */
function groupLocations(locations: CatalogLocation[], owned: { position: { x: number; y: number } }[]): LocationGroup[] {
  const latencyTo = (location: CatalogLocation): number | null => {
    if (owned.length === 0) return null;
    return Math.min(...owned.map((dc) => fiberLatencyMs(dc.position, location.position)));
  };

  const groups = new Map<string, LocationGroup['locations']>();
  for (const location of locations) {
    const list = groups.get(location.country) ?? [];
    list.push({ location, latencyMs: latencyTo(location) });
    groups.set(location.country, list);
  }

  const nearest = (list: LocationGroup['locations']) => Math.min(...list.map((e) => e.latencyMs ?? 0));
  for (const list of groups.values()) list.sort((a, b) => (a.latencyMs ?? 0) - (b.latencyMs ?? 0));

  return [...groups.entries()]
    .map(([country, list]) => ({ country, locations: list }))
    .sort((a, b) => nearest(a.locations) - nearest(b.locations));
}

export function Datacenters() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const catalog = useCatalog();
  const [openLocation, setOpenLocation] = useState(false);
  const [negotiateLocation, setNegotiateLocation] = useState<string | null>(null);
  const [rackFor, setRackFor] = useState<string | null>(null);
  const [rackDetail, setRackDetail] = useState<string | null>(null);

  if (!world) return null;
  const datacenters = Object.values(world.datacenters);
  const occupiedCities = new Set(datacenters.map((d) => d.city));
  const availableLocations = (
    (catalog?.locations ?? []) as unknown as CatalogLocation[]
  ).filter((l) => !occupiedCities.has(l.city));

  /**
   * Freie Standorte nach Land, Land nach Entfernung zum eigenen Netz.
   *
   * Der Katalog reicht inzwischen von Falkenstein bis Phoenix. Ungeordnet wäre
   * das eine Liste von über vierzig Karten, in der die naheliegende Erweiterung
   * neben einer steht, die 9000 km entfernt liegt.
   */
  const locationGroups = groupLocations(availableLocations, datacenters);

  return (
    <>
      <PageHeader
        title="Rechenzentren"
        description="Standorte, Strom, Kühlung und Rackplätze."
        actions={
          <Button variant="primary" onClick={() => setOpenLocation(true)} disabled={availableLocations.length === 0}>
            Standort eröffnen
          </Button>
        }
      />

      <div className="space-y-4">
        {datacenters.map((dc) => {
          const racks = Object.values(world.racks).filter((r) => r.datacenterId === dc.id);
          const devices = [
            ...Object.values(world.servers),
            ...Object.values(world.routers),
            ...Object.values(world.switches),
            ...Object.values(world.firewalls),
            ...Object.values(world.upsUnits),
          ].filter((d) => d.datacenterId === dc.id);

          const usedUnits = devices.reduce((sum, d) => sum + d.heightUnits, 0);
          const totalUnits = racks.reduce((sum, r) => sum + r.units, 0);
          const upsCharge = Object.values(world.upsUnits).filter((u) => u.datacenterId === dc.id);
          const avgCharge = upsCharge.length ? upsCharge.reduce((s, u) => s + u.charge, 0) / upsCharge.length : 0;

          return (
            <Card
              key={dc.id}
              title={
                <span className="flex items-center gap-2">
                  {dc.name}
                  {!dc.utilityPower && <Badge tone="danger">Netzstrom ausgefallen</Badge>}
                </span>
              }
              subtitle={`${dc.city}, ${dc.country} · PUE ${dec(dc.pue, 2)} · ${dec(dc.powerPricePerKwh, 2)} €/kWh`}
              actions={
                <>
                  <Button size="sm" onClick={() => setRackFor(dc.id)} disabled={racks.length >= dc.rackCapacity}>
                    Rack mieten
                  </Button>
                  {!dc.generator.installed && (
                    <Button size="sm" onClick={() => void run({ type: 'dc/installGenerator', datacenterId: dc.id })}>
                      Notstrom nachrüsten
                    </Button>
                  )}
                  {(dc.powerFeeds?.count ?? 1) < 2 && (
                    <Button
                      size="sm"
                      title={`${Math.round(dc.powerCapacityKw * 55).toLocaleString('de-DE')} € einmalig, ${Math.round(dc.powerCapacityKw * 1.8).toLocaleString('de-DE')} €/Monat`}
                      onClick={() => void run({ type: 'dc/addPowerFeed', datacenterId: dc.id })}
                    >
                      Zweite Einspeisung
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => void run({ type: 'dc/expandCooling', datacenterId: dc.id, additionalKw: 20 })}>
                    +20 kW Kühlung
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void run({ type: 'dc/expandPower', datacenterId: dc.id, additionalKw: 20 })}>
                    +20 kW Anschluss
                  </Button>
                </>
              }
            >
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-3">
                  <Meter
                    value={racks.length}
                    max={dc.rackCapacity}
                    label={
                      <>
                        <span>Rackplätze</span>
                        <span className="tabular">
                          {racks.length} / {dc.rackCapacity}
                        </span>
                      </>
                    }
                  />
                  <Meter
                    value={usedUnits}
                    max={totalUnits}
                    label={
                      <>
                        <span>Höheneinheiten</span>
                        <span className="tabular">
                          {usedUnits} / {totalUnits} HE
                        </span>
                      </>
                    }
                  />
                </div>

                <div className="space-y-3">
                  <Meter
                    value={dc.coolingCapacityKw * 0.6}
                    max={dc.coolingCapacityKw}
                    label={
                      <>
                        <span>Kühlleistung</span>
                        <span className="tabular">{dec(dc.coolingCapacityKw, 1)} kW</span>
                      </>
                    }
                  />
                  <Meter
                    value={avgCharge}
                    max={1}
                    invert
                    label={
                      <>
                        <span>USV-Ladezustand</span>
                        <span className="tabular">{upsCharge.length ? `${Math.round(avgCharge * 100)} %` : 'keine USV'}</span>
                      </>
                    }
                  />

                  {/* Einzelne USV-Einheiten – bisher nur als Durchschnitt sichtbar,
                      damit aber auch nicht abgebbar. */}
                  {upsCharge.length > 0 && (
                    <div className="space-y-1">
                      {upsCharge.map((ups) => (
                        <div key={ups.id} className="surface-muted flex items-center gap-2 p-1.5 text-2xs">
                          <StatusDot tone={ups.charge > 0.5 ? 'success' : ups.charge > 0.2 ? 'warning' : 'danger'} />
                          <span className="min-w-0 flex-1 truncate">{ups.name}</span>
                          {/* Zentrale USV stehen in keinem Rack – das unterscheidet
                              sie hier von der rackgebundenen Kleinvariante. */}
                          {ups.rackId === null && <Badge tone="muted">zentral</Badge>}
                          <span className="tabular text-panel-500">
                            {ups.outputKw} kW · {Math.round(ups.charge * 100)} %
                          </span>
                          <SellDeviceButton kind="ups" id={ups.id} name={ups.name} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Anschlussleistung">{dc.powerCapacityKw} kW</Field>
                  <Field label="Einspeisung">
                    {(dc.powerFeeds?.count ?? 1) > 1 ? (
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone="success" />
                        A+B redundant
                      </span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">einfach</span>
                    )}
                  </Field>
                  <Field label="Notstrom">
                    {dc.generator.installed ? (
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={dc.generator.fuelPercent > 20 ? 'success' : 'warning'} />
                        {Math.round(dc.generator.fuelPercent)} % Treibstoff
                      </span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">nicht vorhanden</span>
                    )}
                  </Field>
                  <Field label="Monatsmiete">{money(dc.monthlyRent)}</Field>
                  <Field label="Umgebungstemperatur">{Math.round(dc.ambientTempC)} °C</Field>
                  <Field label="Carrier vor Ort">{dc.carriers.join(', ')}</Field>
                  <Field label="Geräte">{devices.length}</Field>
                </div>
              </div>

              {/* Der Mietvertrag hinter dem Standort: Konditionen, Kündigung, Umzug. */}
              <ContractPanel dc={dc} />

              {racks.length > 0 && (
                <div className="mt-4 border-t divider pt-3">
                  <p className="label mb-2">Racks</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {racks.map((rack) => {
                      const rackDevices = devices.filter((d) => d.rackId === rack.id);
                      const used = rackDevices.reduce((s, d) => s + d.heightUnits, 0);
                      const watts = rackDevices.reduce((s, d) => s + ('maxWatt' in d ? (d as { maxWatt: number }).maxWatt : 0), 0);
                      return (
                        <button
                          key={rack.id}
                          type="button"
                          onClick={() => setRackDetail(rack.id)}
                          className="surface-muted p-3 text-left transition hover:bg-panel-500/10"
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <span className="text-sm font-medium">{rack.name}</span>
                            <span className="text-2xs text-panel-500">{money(rack.monthlyCost)}/Mon.</span>
                          </div>
                          <Meter
                            value={used}
                            max={rack.units}
                            label={
                              <>
                                {/*
                                  Nicht "N Geräte": In einem Rack stehen Server neben
                                  Router, Switch und USV. Die nackte Summe liest sich
                                  wie eine Serverzahl und stimmt dann nie.
                                */}
                                <span>{describeRackContents(world, rack.id)}</span>
                                <span className="tabular">
                                  {used}/{rack.units} HE
                                </span>
                              </>
                            }
                          />
                          <p className="mt-1.5 text-2xs text-panel-500">
                            Strombudget {dec(watts / 1000, 1)} / {rack.powerBudgetKw} kW · <span className="underline">ansehen</span>
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Standort eröffnen */}
      <Modal open={openLocation} onClose={() => setOpenLocation(false)} title="Neuen Standort eröffnen" wide>
        <div className="space-y-2">
          <Notice tone="info">
            Ein zweiter Standort verteilt das Risiko und senkt Latenzen – erfordert aber eigene Netzanbindung und Personal.
          </Notice>
          {locationGroups.map(({ country, locations }) => (
            <div key={country} className="space-y-2 pt-2">
              <p className="label">{COUNTRY_NAMES[country] ?? country}</p>
              {locations.map(({ location, latencyMs }) => (
                <div key={location.key} className="surface-muted flex flex-wrap items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{location.city}</p>
                    <p className="text-xs text-panel-500 dark:text-panel-400">{location.description}</p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="tabular font-medium">{money(location.setupCost)} einmalig</p>
                    <p className="tabular text-panel-500">
                      {money(location.monthlyRent)}/Mon. · {dec(location.powerPricePerKwh, 2)} €/kWh
                    </p>
                    <p className="tabular text-panel-500">
                      Potenzial {dec(location.marketPotential, 2)} · {location.ambientTempC} °C
                    </p>
                    {/* Die Entfernung zum eigenen Netz entscheidet mit: Eine
                        Backbone-Strecke über den Atlantik ist teuer und träge. */}
                    {latencyMs !== null && (
                      <p className={clsx('tabular', latencyMs > 40 ? 'text-amber-600 dark:text-amber-500' : 'text-panel-500')}>
                        {dec(latencyMs, 1)} ms zum nächsten eigenen Standort
                      </p>
                    )}
                  </div>
                  {/*
                    Zwei Wege, denselben Standort zu bekommen: sofort zum
                    Listenpreis oder verhandelt. Sie stehen bewusst
                    nebeneinander – wer es eilig hat, soll sehen, worauf er
                    verzichtet.
                  */}
                  <Button size="sm" onClick={() => setNegotiateLocation(location.key)}>
                    Verhandeln
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    title="Zwölf Monate Laufzeit zum Listenpreis"
                    onClick={async () => {
                      const result = await run({ type: 'dc/open', locationKey: location.key });
                      if (result.ok) setOpenLocation(false);
                    }}
                  >
                    Sofort eröffnen
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Modal>

      {/* Rack mieten */}
      <Modal open={rackFor !== null} onClose={() => setRackFor(null)} title="Rack anmieten">
        <div className="space-y-2">
          {((catalog?.racks ?? []) as unknown as { key: string; name: string; units: number; powerBudgetKw: number; monthlyCost: number; setupCost: number; description: string }[]).map(
            (model) => (
              <div key={model.key} className="surface-muted flex flex-wrap items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{model.name}</p>
                  <p className="text-xs text-panel-500 dark:text-panel-400">{model.description}</p>
                </div>
                <div className="text-right text-xs">
                  <p className="tabular font-medium">{money(model.monthlyCost)}/Mon.</p>
                  <p className="tabular text-panel-500">{money(model.setupCost)} Einrichtung</p>
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={async () => {
                    const result = await run({ type: 'rack/rent', datacenterId: rackFor as never, modelKey: model.key });
                    if (result.ok) setRackFor(null);
                  }}
                >
                  Mieten
                </Button>
              </div>
            ),
          )}
        </div>
      </Modal>

      <RackDetail rackId={rackDetail} onClose={() => setRackDetail(null)} />

      {negotiateLocation && (
        <NegotiationDialog
          locationKey={negotiateLocation}
          onClose={() => {
            setNegotiateLocation(null);
            setOpenLocation(false);
          }}
        />
      )}
    </>
  );
}

/**
 * Gemeinsame Form aller Rackgeräte.
 *
 * Server, Router, Switch, Firewall und USV haben keinen gemeinsamen Obertyp,
 * teilen aber genau die Felder, die eine Bestückungsliste braucht.
 */
type RackDevice = {
  id: string;
  name: string;
  rackId: string;
  rackUnit: number;
  heightUnits: number;
  state: DeviceState;
  temperatureC: number;
  health: number;
  pendingTicks: number;
  maxWatt?: number;
};

/** Alle Geräte eines Racks, unabhängig von ihrer Art, von oben nach unten. */
function rackContents(
  world: NonNullable<ReturnType<typeof useGame.getState>['world']>,
  rackId: string,
): { kind: DeviceKindKey; kindLabel: string; device: RackDevice }[] {
  const kinds: { kind: DeviceKindKey; label: string; store: Record<string, unknown> }[] = [
    { kind: 'server', label: 'Server', store: world.servers },
    { kind: 'router', label: 'Router', store: world.routers },
    { kind: 'switch', label: 'Switch', store: world.switches },
    { kind: 'firewall', label: 'Firewall', store: world.firewalls },
    { kind: 'ups', label: 'USV', store: world.upsUnits },
  ];

  return kinds
    .flatMap(({ kind, label, store }) =>
      (Object.values(store) as RackDevice[])
        .filter((device) => device.rackId === rackId)
        .map((device) => ({ kind, kindLabel: label, device })),
    )
    .sort((a, b) => b.device.rackUnit - a.device.rackUnit);
}

/** "3 Server, 1 Router, 1 Switch" statt einer nackten Gerätezahl. */
function describeRackContents(world: NonNullable<ReturnType<typeof useGame.getState>['world']>, rackId: string): string {
  const counts = new Map<string, number>();
  for (const entry of rackContents(world, rackId)) {
    counts.set(entry.kindLabel, (counts.get(entry.kindLabel) ?? 0) + 1);
  }
  if (counts.size === 0) return 'leer';
  return [...counts].map(([label, count]) => `${count} ${label}`).join(', ');
}

/**
 * Blick in ein Rack.
 *
 * Ohne diese Ansicht ist ein Rack eine Zahl: "6 Geräte", ohne zu sagen welche.
 * Wer drei Server gekauft hat und sechs Geräte gezählt sieht, hält das
 * zwangsläufig für einen Fehler – dabei stehen dort noch Router, Switch und
 * USV. Deshalb zeigt die Ansicht die Bestückung von oben nach unten, so wie
 * man vor dem Rack steht, und lässt jedes Gerät direkt anfassen.
 */
function RackDetail({ rackId, onClose }: { rackId: string | null; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const load = useServerLoad();
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const [view, setView] = useState<'liste' | 'raum'>('liste');

  if (!rackId || !world) return null;
  const rack = world.racks[rackId as never];
  if (!rack) return null;

  const contents = rackContents(world, rackId);
  const used = contents.reduce((sum, entry) => sum + entry.device.heightUnits, 0);
  const watts = contents.reduce((sum, entry) => sum + ('maxWatt' in entry.device ? (entry.device as { maxWatt: number }).maxWatt : 0), 0);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        wide
        title={`${rack.name} — Bestückung`}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Schließen
            </Button>
            {/*
              Ein Rack ist gemietet, nicht gekauft – zurückgeben heißt, die
              Monatsmiete loszuwerden. Der Weg dorthin fehlte in der Oberfläche
              ganz: Das Kommando gab es, den Knopf nicht.
            */}
            <ReleaseRackButton rackId={rack.id} name={rack.name} occupied={contents.length} onReleased={onClose} />
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Höheneinheiten">
              {used} / {rack.units} HE belegt
            </Field>
            <Field label="Strombudget">
              {dec(watts / 1000, 1)} / {rack.powerBudgetKw} kW
            </Field>
            <Field label="Inhalt">{describeRackContents(world, rackId)}</Field>
            <Field label="Monatsmiete">{money(rack.monthlyCost)}</Field>
          </div>

          <Tabs
            tabs={[
              { id: 'liste', label: 'Bestückung' },
              { id: 'raum', label: '3D-Ansicht' },
            ]}
            active={view}
            onChange={(id) => setView(id as 'liste' | 'raum')}
          />

          {view === 'raum' ? (
            <Suspense
              fallback={
                <div className="flex h-[22rem] items-center justify-center rounded-md bg-panel-950">
                  <Spinner className="h-5 w-5" />
                </div>
              }
            >
              <RackViewer
                rackId={rackId}
                onOpenDevice={(id) => {
                  const server = world.servers[id as never];
                  if (server) setSelectedServer(server);
                }}
              />
            </Suspense>
          ) : contents.length === 0 ? (
            <EmptyState
              title="Das Rack ist leer"
              description="Beim Kauf von Servern, Routern, Switches oder USV lässt sich dieses Rack als Einbauort wählen."
            />
          ) : (
            <ul className="space-y-1">
              {contents.map((entry, i) => (
                <li key={`${entry.kind}-${entry.device.id}`} className="surface-muted flex flex-wrap items-center gap-2 p-2 text-xs">
                  <span className="tabular w-16 shrink-0 text-panel-500">
                    HE {entry.device.rackUnit + 1}
                    {entry.device.heightUnits > 1 ? `–${entry.device.rackUnit + entry.device.heightUnits}` : ''}
                  </span>
                  {/*
                    Verschieben tauscht nur mit dem Nachbarn im Stapel – kostenlos und
                    ohne Ausfall, denn das Gerät bleibt im selben Rack, nur die Position
                    darin ändert sich.
                  */}
                  <span className="flex shrink-0 gap-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={i === 0}
                      title="Eine Position nach oben"
                      onClick={() => void run({ type: 'dc/reorderDevice', kind: entry.kind, id: entry.device.id, direction: 'up' })}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={i === contents.length - 1}
                      title="Eine Position nach unten"
                      onClick={() => void run({ type: 'dc/reorderDevice', kind: entry.kind, id: entry.device.id, direction: 'down' })}
                    >
                      ↓
                    </Button>
                  </span>
                  <Badge tone={entry.kind === 'server' ? 'neutral' : 'muted'}>{entry.kindLabel}</Badge>
                  <span className="min-w-0 flex-1 truncate font-medium">{entry.device.name}</span>
                  <DeviceStatus device={entry.device as never} />
                  {/* Im Rack interessiert vor allem, welcher Knoten noch Platz hat. */}
                  {entry.kind === 'server' && (
                    <span className="w-24 shrink-0">
                      <FillCell row={load.get(entry.device.id)} />
                    </span>
                  )}
                  {entry.kind === 'server' && (
                    <Button size="sm" onClick={() => setSelectedServer(world.servers[entry.device.id as never])}>
                      Öffnen
                    </Button>
                  )}
                  <RelocateDeviceButton kind={entry.kind} id={entry.device.id} name={entry.device.name} />
                  <SellDeviceButton kind={entry.kind} id={entry.device.id} name={entry.device.name} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      <ServerDetail server={selectedServer} onClose={() => setSelectedServer(null)} />
    </>
  );
}

/**
 * Ein Rack zurückgeben.
 *
 * Leer ist die Bedingung, und sie steht hier auch dann da, wenn sie nicht
 * erfüllt ist: Ein ausgegrauter Knopf ohne Begründung ist eine Sackgasse, ein
 * ausgegrauter mit Begründung ein Arbeitsauftrag.
 */
function ReleaseRackButton({
  rackId,
  name,
  occupied,
  onReleased,
}: {
  rackId: string;
  name: string;
  occupied: number;
  onReleased: () => void;
}) {
  const run = useGame((s) => s.run);
  const [asking, setAsking] = useState(false);

  if (occupied > 0) {
    return (
      <Button disabled title={`${occupied} Geräte stehen noch darin – erst umbauen, verlagern oder verkaufen.`}>
        Zurückgeben
      </Button>
    );
  }

  return (
    <>
      <Button variant="danger" onClick={() => setAsking(true)}>
        Zurückgeben
      </Button>
      <Modal open={asking} onClose={() => setAsking(false)} title={`${name} zurückgeben?`}>
        <div className="space-y-4">
          <Notice tone="info" title="Die Miete entfällt ab sofort">
            Das Rack geht an den Standortbetreiber zurück. Eine Rückzahlung der Einrichtungskosten gibt es nicht – wer es
            später wieder braucht, mietet neu.
          </Notice>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                const result = await run({ type: 'rack/release', rackId: rackId as never });
                setAsking(false);
                if (result.ok) onReleased();
              }}
            >
              Zurückgeben
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Server
// ---------------------------------------------------------------------------

export function Servers() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const metrics = useMetrics();
  const catalog = useCatalog();
  const load = useServerLoad();
  const [buying, setBuying] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [selected, setSelected] = useState<Server | null>(null);

  const servers = useMemo(() => (world ? Object.values(world.servers) : []), [world]);
  if (!world || !metrics) return null;

  const columns: Column<Server>[] = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (s) => s.name,
      render: (s) => (
        <div>
          <p className="font-medium">{s.name}</p>
          <p className="text-2xs text-panel-500">{world.datacenters[s.datacenterId]?.city}</p>
        </div>
      ),
    },
    { key: 'state', header: 'Zustand', sortValue: (s) => s.state, render: (s) => <DeviceStatus device={s} /> },
    { key: 'role', header: 'Rolle', sortValue: (s) => s.role, render: (s) => <Badge tone={s.role === 'unassigned' ? 'muted' : 'neutral'}>{ROLE_LABEL[s.role]}</Badge> },
    {
      key: 'os',
      header: 'Betriebssystem',
      hideOnMobile: true,
      sortValue: (s) => s.os ?? '',
      render: (s) => (s.os ? OS_LABEL[s.os] : <span className="text-amber-600 dark:text-amber-400">nicht installiert</span>),
    },
    {
      key: 'spec',
      header: 'Ausstattung',
      hideOnMobile: true,
      render: (s) => (
        <span className="text-xs text-panel-500 dark:text-panel-400">
          {s.cpuCores} Kerne · {bytes(s.ramGb)} RAM · {bytes(s.ssdGb + s.hddGb)}
        </span>
      ),
    },
    {
      key: 'fill',
      header: 'Belegung',
      width: '9rem',
      sortValue: (s) => load.get(s.id)?.fill ?? -1,
      render: (s) => (
        <div className="space-y-1">
          <FillCell row={load.get(s.id)} />
          <p className="text-2xs text-panel-500">
            {s.contractIds.length + s.vpsIds.length} {s.role === 'vps-host' ? 'VPS' : 'Verträge'}
          </p>
        </div>
      ),
    },
    {
      key: 'load',
      header: 'Last',
      width: '8rem',
      hideOnMobile: true,
      sortValue: (s) => load.get(s.id)?.load ?? -1,
      render: (s) => <LoadCell row={load.get(s.id)} />,
    },
    { key: 'health', header: 'Zustand/Temp.', align: 'right', sortValue: (s) => s.health, render: (s) => <HealthCell health={s.health} temperatureC={s.temperatureC} /> },
  ];

  return (
    <>
      <PageHeader
        title="Server"
        description="Bestand, Rollen und Auslastung der Rechenknoten."
        actions={
          <>
            <ConsolidateButton />
            {/*
              Zwei Wege zu einem Server: aus dem Katalog nehmen oder selbst
              zusammenstellen. Der Katalog ist schneller, der Konfigurator
              trifft genauer – deshalb stehen beide da.
            */}
            <Button onClick={() => setConfiguring(true)}>Server konfigurieren</Button>
            <Button variant="primary" onClick={() => setBuying(true)}>
              Server kaufen
            </Button>
          </>
        }
      />

      <StatGrid cols={5}>
        <Stat
          label="Server gesamt"
          value={num(servers.length)}
          hint={
            `${servers.filter((s) => s.state === 'online').length} online` +
            // Reserve gehört in die Kurzfassung: Sie erklärt die Lücke zwischen
            // „gesamt“ und „online“, ohne dass jemand die Liste durchgeht.
            (servers.some((s) => s.standby) ? `, ${servers.filter((s) => s.standby).length} in Reserve` : '')
          }
        />
        <Stat
          label="CPU-Auslastung"
          value={`${Math.round((metrics.derived.capacity.cpuCoresUsed / Math.max(1, metrics.derived.capacity.cpuCoresTotal)) * 100)} %`}
          hint={`${num(metrics.derived.capacity.cpuCoresUsed)} von ${num(metrics.derived.capacity.cpuCoresTotal)} Kernen`}
        />
        <Stat
          label="Speicherbelegung"
          value={`${Math.round((metrics.derived.capacity.ramGbUsed / Math.max(1, metrics.derived.capacity.ramGbTotal)) * 100)} %`}
          hint={`${bytes(metrics.derived.capacity.ramGbUsed)} von ${bytes(metrics.derived.capacity.ramGbTotal)}`}
        />
        {/*
          Der globale Durchschnitt verdeckt den Einzelfall: Ein Bestand bei 40 %
          kann problemlos drei Knoten enthalten, die nichts mehr annehmen. Diese
          Zahl macht sie sichtbar, bevor die erste Bestellung abgelehnt wird.
        */}
        <Stat
          label="Nahe der Grenze"
          value={num(servers.filter((s) => (load.get(s.id)?.fill ?? 0) >= 0.9).length)}
          tone={servers.some((s) => (load.get(s.id)?.fill ?? 0) >= 0.9) ? 'warning' : 'neutral'}
          hint="ab 90 % Belegung"
        />
        <Stat
          label="Ohne Betriebssystem"
          value={num(servers.filter((s) => s.os === null).length)}
          tone={servers.some((s) => s.os === null) ? 'warning' : 'neutral'}
          hint="können keine Kunden aufnehmen"
        />
      </StatGrid>

      <div className="mt-4 space-y-3">
        <RetirementNotice />
        <ReplacementHint onSelect={(id) => setSelected(world.servers[id as never] ?? null)} />
      </div>

      <Card className="mt-4" padded={false}>
        <DataTable columns={columns} rows={servers} rowKey={(s) => s.id} onRowClick={setSelected} empty="Noch keine Server vorhanden." />
      </Card>

      <BuyHardwareDialog
        open={buying}
        onClose={() => setBuying(false)}
        kind="server"
        catalog={catalog}
        onBuy={async (rackId, modelKey, role) => {
          const result = await run({ type: 'hw/buyServer', rackId: rackId as never, modelKey, role: role as ServerRole });
          return result.ok;
        }}
      />

      <ServerConfiguratorDialog open={configuring} onClose={() => setConfiguring(false)} catalog={catalog} />

      <ServerDetail server={selected} onClose={() => setSelected(null)} />
    </>
  );
}

/**
 * Server abschalten – mit Rückfrage, solange Kunden darauf liegen.
 *
 * Ohne Kunden ist es ein Klick: Der Knoten geht in Reserve, zieht keinen Strom
 * mehr und bleibt auch nach einem Stromausfall aus. Mit Kunden ist es eine
 * Entscheidung mit Folgen, und die soll ausgesprochen werden, bevor sie fällt.
 */
function PowerOffButton({ server }: { server: Server }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [asking, setAsking] = useState(false);

  const busy =
    server.contractIds.filter((id) => world?.contracts[id] && world.contracts[id].state !== 'cancelled').length +
    server.vpsIds.filter((id) => !!world?.vpsInstances[id]).length;

  return (
    <>
      <Button onClick={() => (busy > 0 ? setAsking(true) : void run({ type: 'hw/power', kind: 'server', id: server.id, on: false }))}>
        Ausschalten
      </Button>

      <Modal open={asking} onClose={() => setAsking(false)} title={`${server.name} ausschalten?`}>
        <div className="space-y-4">
          <Notice tone="danger" title={`${busy} Dienste liegen auf diesem Server`}>
            Abgeschaltet sind sie offline. Das zählt als Ausfallzeit, drückt die Zufriedenheit und kann SLA-Gutschriften
            auslösen. Der saubere Weg ist „Räumen“: Die Dienste ziehen auf andere Knoten, danach lässt sich der Server
            gefahrlos stilllegen.
          </Notice>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Abbrechen
            </Button>
            <EvacuateServerButton serverId={server.id} />
            <Button
              variant="danger"
              onClick={async () => {
                await run({ type: 'hw/power', kind: 'server', id: server.id, on: false, force: true });
                setAsking(false);
              }}
            >
              Trotzdem ausschalten
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function ServerDetail({ server, onClose }: { server: Server | null; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [os, setOs] = useState<OperatingSystem>('linux-debian');
  const [role, setRole] = useState<ServerRole>('shared-hosting');
  const [tab, setTab] = useState<'overview' | 'console'>('overview');
  const underAttack = useActiveEvents().some((e) => e.ddos?.serverId === server?.id);

  useEffect(() => {
    if (server) {
      setOs(server.os ?? 'linux-debian');
      setRole(server.role);
    }
  }, [server]);

  // Ein anderer Server öffnet wieder in der Übersicht.
  useEffect(() => setTab('overview'), [server?.id]);

  if (!server || !world) return null;
  const current = world.servers[server.id] ?? server;
  const busy = current.contractIds.length > 0 || current.vpsIds.length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={current.name}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          {current.state === 'failed' && (
            <Button variant="primary" onClick={() => void run({ type: 'hw/repair', kind: 'server', id: current.id })}>
              Reparieren
            </Button>
          )}
          {current.state === 'offline' ? (
            <Button variant="primary" onClick={() => void run({ type: 'hw/power', kind: 'server', id: current.id, on: true })}>
              Einschalten
            </Button>
          ) : (
            <>
              <Button onClick={() => void run({ type: 'hw/reboot', kind: 'server', id: current.id })}>Neu starten</Button>
              {/*
                Der Ausschalter fehlte bisher ganz – ein Server ließ sich nur
                verkaufen, nicht stilllegen. Genau das braucht aber, wer drei
                Geräte hat und eines davon nutzt.
              */}
              <PowerOffButton server={current} />
            </>
          )}
          {/*
            Solange Kunden darauf liegen, ist der Verkauf gesperrt – hier steht
            deshalb der Weg dorthin: räumen, vormerken, und der Server gibt sich
            selbst ab. „Räumen“ steht bewusst vor der Vormerkung: Es ist der
            Schritt, der tatsächlich etwas bewegt.
          */}
          {busy && <EvacuateServerButton serverId={current.id} />}
          {busy &&
            (current.retirement ? (
              <Button onClick={() => void run({ type: 'server/cancelRetirement', serverId: current.id })}>Vormerkung aufheben</Button>
            ) : (
              <Button onClick={() => void run({ type: 'server/retire', serverId: current.id })}>Zur Abgabe vormerken</Button>
            ))}
          <SellDeviceButton kind="server" id={current.id} name={current.name} size="md" onSold={onClose} />
        </>
      }
    >
      <Tabs
        tabs={[
          { id: 'overview', label: 'Übersicht' },
          { id: 'console', label: 'Konsole', badge: underAttack ? <Badge tone="danger">Angriff</Badge> : undefined },
        ]}
        active={tab}
        onChange={(id) => setTab(id as 'overview' | 'console')}
      />
      {tab === 'console' ? (
        <ServerConsole server={current} className="mt-4" />
      ) : (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Zustand">
              <DeviceStatus device={current} />
            </Field>
            <Field label="Modell">{current.model}</Field>
            <Field label="Standort">{world.datacenters[current.datacenterId]?.name}</Field>
            <Field label="Rack">{world.racks[current.rackId]?.name}</Field>
            <Field label="CPU">
              {current.cpuModel} ({current.cpuCores} Kerne)
            </Field>
            <Field label="RAM">{bytes(current.ramGb)}</Field>
            <Field label="SSD / HDD">
              {bytes(current.ssdGb)} / {bytes(current.hddGb)}
            </Field>
            <Field label="Leistungsaufnahme">
              {watt(current.idleWatt)} – {watt(current.maxWatt)}
            </Field>
            <Field label="Management-IP">
              <Mono>{current.managementIp ?? '–'}</Mono>
            </Field>
            <Field label="Restzustand">
              <HealthCell health={current.health} temperatureC={current.temperatureC} />
            </Field>
            <Field label="Angeschafft">Tick {num(current.purchasedAtTick)}</Field>
            <Field label="Kaufpreis">{money(current.purchasePrice)}</Field>
          </div>

          <div className="grid gap-3 border-t divider pt-3 sm:grid-cols-2">
            <div>
              <Select label="Betriebssystem" value={os} onChange={(e) => setOs(e.target.value as OperatingSystem)} disabled={busy}>
                {Object.entries(OS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <Button
                size="sm"
                className="mt-2"
                disabled={busy}
                onClick={() => void run({ type: 'server/installOs', serverId: current.id, os })}
              >
                Neu installieren
              </Button>
            </div>
            <div>
              <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value as ServerRole)} disabled={busy}>
                {Object.entries(ROLE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <Button size="sm" className="mt-2" disabled={busy} onClick={() => void run({ type: 'server/setRole', serverId: current.id, role })}>
                Rolle übernehmen
              </Button>
            </div>
          </div>

          {busy && (
            <Notice tone="warning">
              Der Server ist belegt ({current.contractIds.length} Verträge, {current.vpsIds.length} VPS). Rolle und Betriebssystem lassen sich
              erst nach dem Umzug oder der Kündigung dieser Leistungen ändern.
            </Notice>
          )}

          <UtilizationPanel server={current} />
          <UplinkPanel server={current} />
          <PartsPanel server={current} />
        </div>
      )}
    </Modal>
  );
}

/**
 * Auslastung des Servers in absoluten Zahlen.
 *
 * Der Live-Auszug trägt nur Quoten – für die Liste reicht das, im Dialog nicht:
 * Wer entscheiden soll, ob er aufrüstet, umzieht oder einen neuen Knoten kauft,
 * braucht die Zahlen dahinter. Sie stehen im geladenen Weltzustand und werden
 * hier gerechnet; die Rechnung ist dieselbe wie im Kern.
 */
function UtilizationPanel({ server }: { server: Server }) {
  const world = useGame((s) => s.world);
  const live = useServerLoad().get(server.id);

  const utilization = useMemo(
    () => (world ? serverUtilization(world, server.id, activeModifiers(world).computePerformance) : null),
    [world, server.id],
  );
  if (!utilization) return null;

  /*
   * `describe` schreibt "a von b", wenn zwei Zahlen da sind, sonst nur eine –
   * und setzt die Einheit einmal ans Ende statt hinter jede Zahl. Sonst stand
   * dort "72,0 Kerne von 72,0 Kerne", und die Einheit fiel im Dativ um.
   */
  const kerne = (used: number, of?: number) =>
    of === undefined ? `${dec(used, 1)} Kerne` : `${dec(used, 1)} von ${dec(of, 1)} Kernen`;
  const platz = (used: number, of?: number) => (of === undefined ? bytes(used) : `${bytes(used)} von ${bytes(of)}`);

  const rows = [
    { key: 'cpu' as const, label: 'CPU', usage: utilization.cpu, describe: kerne },
    { key: 'ram' as const, label: 'RAM', usage: utilization.ram, describe: platz },
    { key: 'ssd' as const, label: 'SSD', usage: utilization.ssd, describe: platz },
    { key: 'hdd' as const, label: 'HDD', usage: utilization.hdd, describe: platz },
  ].filter((row) => row.usage.installed > 0);

  const overcommitted = rows.some((row) => row.usage.limit > row.usage.installed);

  return (
    <div className="border-t divider pt-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400">Auslastung</h3>

      {/*
        Drei Zahlen, und jede muss sagen, was sie ist.

        Vorher stand hier "72,0 Kerne von 72,0 Kerne vergeben · 24,0 Kerne
        verbaut". Die 72 sind die dreifache Überbuchung von 24 – das stand
        nirgends, also las es sich wie ein Rechenfehler. Der Balken misst weiter
        gegen die Verkaufsgrenze, denn das ist die Frage, die er beantwortet
        ("passt noch ein Kunde drauf?"); der Satz darunter nennt jetzt die
        Herkunft der Grenze, statt sie als gegeben hinzustellen.
      */}
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((row) => {
          const faktor = row.usage.installed > 0 ? row.usage.limit / row.usage.installed : 1;
          // Mehr zugesagt als vorhanden: bei Platten die Stelle, an der aus
          // Überbuchung ein Risiko wird – Daten lassen sich nicht teilen.
          const ueberzeichnet = row.usage.used > row.usage.installed;
          return (
            <div key={row.key}>
              <Meter
                value={row.usage.ratio}
                max={1}
                label={
                  <>
                    <span>
                      {row.label}
                      {row.key === utilization.bottleneck && rows.length > 1 && <span className="ml-1 text-panel-400">· engste Stelle</span>}
                    </span>
                    <span className="tabular">{Math.round(row.usage.ratio * 100)} %</span>
                  </>
                }
              />
              <p className="mt-1 text-2xs text-panel-500 dark:text-panel-400">
                {row.describe(row.usage.used, row.usage.limit)} zugesagt
                <br />
                {row.describe(row.usage.installed)} verbaut
                {faktor > 1 && `, ${dec(faktor, Number.isInteger(faktor) ? 0 : 2)}× überbucht`}
              </p>
              {ueberzeichnet && (row.key === 'ssd' || row.key === 'hdd') && (
                <p className="mt-0.5 text-2xs text-amber-600 dark:text-amber-400">
                  Mehr zugesagt als verbaut – belegen alle Kunden ihr Kontingent, geht der Platz aus.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Rechenlast">
          <span className="tabular">{Math.round((live?.load ?? utilization.load) * 100)} %</span>
        </Field>
        <Field label="Leistungsaufnahme">{watt(live?.watt ?? utilization.watt)}</Field>
        <Field label="VPS">{num(utilization.vpsCount)}</Field>
        <Field label="Verträge">{num(utilization.contractCount)}</Field>
      </div>

      {overcommitted && (
        <p className="mt-2 text-2xs text-panel-500 dark:text-panel-400">
          Die Grenze liegt über dem Verbauten: Für diese Rolle wird planmäßig überbucht, weil sich Kunden dieselben Reserven teilen. Die
          Rechenlast oben zeigt, was davon gerade tatsächlich abgerufen wird.
        </p>
      )}
    </div>
  );
}

/**
 * Netzanbindung des Servers.
 *
 * Ohne Uplink liefert der Server nichts aus – deshalb steht hier nicht nur der
 * Zustand, sondern direkt die Auswahl eines freien Ports.
 */
function UplinkPanel({ server }: { server: Server }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [target, setTarget] = useState('');

  const options = useMemo(() => {
    if (!world) return [];
    const list: { value: string; label: string }[] = [];
    for (const sw of Object.values(world.switches)) {
      if (sw.datacenterId !== server.datacenterId) continue;
      for (const port of sw.ports) {
        if (port.connectedDevice || port.linkId) continue;
        list.push({
          value: `${sw.id}:${port.index}`,
          label: `${sw.name} Port ${port.index} · ${port.speedMbps >= 1000 ? `${port.speedMbps / 1000} G` : `${port.speedMbps} M`} · VLAN ${port.vlanId}`,
        });
      }
    }
    return list;
  }, [world, server.datacenterId]);

  useEffect(() => {
    if (options.length > 0 && !options.some((o) => o.value === target)) setTarget(options[0].value);
  }, [options, target]);

  if (!world) return null;
  const uplink = server.uplink;
  const sw = uplink ? world.switches[uplink.switchId] : null;
  const port = sw && uplink ? sw.ports[uplink.portIndex] : null;

  async function connect() {
    const [switchId, portIndex] = target.split(':');
    await run({
      type: uplink ? 'cable/repatch' : 'cable/connect',
      kind: 'server',
      deviceId: server.id,
      switchId: switchId as never,
      portIndex: Number(portIndex),
    });
  }

  return (
    <div className="border-t divider pt-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400">Netzanbindung</h3>

      {uplink && sw ? (
        <div className="flex flex-wrap items-center gap-3">
          <Field label="Angeschlossen an">
            {sw.name} Port {uplink.portIndex}
          </Field>
          <Field label="VLAN">{port?.vlanId ?? '–'}</Field>
          <Field label="Portstatus">
            {port?.enabled === false ? <Badge tone="danger">abgeschaltet</Badge> : <Badge tone="success">aktiv</Badge>}
          </Field>
          <Button size="sm" variant="ghost" onClick={() => void run({ type: 'cable/disconnect', kind: 'server', deviceId: server.id })}>
            Abziehen
          </Button>
        </div>
      ) : (
        <Notice tone="warning" title="Kein Uplink">
          Der Server hängt an keinem Switch und ist deshalb nicht erreichbar – er nimmt auch keine Neukunden auf.
        </Notice>
      )}

      {options.length > 0 && (
        <div className="mt-2 flex items-end gap-2">
          <Select label={uplink ? 'Umstecken auf' : 'Anschließen an'} value={target} onChange={(e) => setTarget(e.target.value)} className="flex-1">
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button size="sm" variant="primary" onClick={connect}>
            {uplink ? 'Umstecken' : 'Anschließen'}
          </Button>
        </div>
      )}
      {options.length === 0 && !uplink && (
        <p className="mt-2 text-xs text-panel-500 dark:text-panel-400">
          Am Standort ist kein freier Switch-Port verfügbar. Erst einen Switch beschaffen oder Ports freimachen.
        </p>
      )}
    </div>
  );
}

/**
 * Bestückung und Ausbau.
 *
 * Zeigt jeden Steckplatz einzeln – das ist der Punkt: Wer sehen will, ob noch
 * etwas hineinpasst, zählt keine Gigabyte, sondern freie Plätze.
 */
function PartsPanel({ server }: { server: Server }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [install, setInstall] = useState<string[]>([]);
  const [remove, setRemove] = useState<{ kind: PartKind; slot: number }[]>([]);
  const [acceptDowntime, setAcceptDowntime] = useState(false);

  const model = findServerModel(server.model);
  const usage = slotUsage(server);
  const stock = world?.inventory[server.datacenterId] ?? {};

  const preview = useMemo(() => {
    if (!world) return null;
    if (install.length === 0 && remove.length === 0) return null;
    return previewUpgrade(world, {
      serverId: server.id,
      install: install.map((modelKey) => ({ modelKey })),
      remove,
      acceptDowntime,
    });
  }, [world, server.id, install, remove, acceptDowntime]);

  if (!world || !model) return null;

  const job = server.maintenance;
  if (job) {
    return (
      <div className="border-t divider pt-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400">Wartungsfenster läuft</h3>
        <Notice tone="info" title={job.summary}>
          Noch {server.pendingTicks} Stunden. {job.affectedContractIds.length > 0 && `${job.affectedContractIds.length} Verträge sind währenddessen offline.`}
        </Notice>
        <Button size="sm" className="mt-2" variant="ghost" onClick={() => void run({ type: 'server/cancelUpgrade', serverId: server.id })}>
          Abbrechen und Bauteile zurücklegen
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t divider pt-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400">Bestückung</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        {(Object.keys(usage) as PartKind[]).map((kind) => {
          const group = usage[kind];
          if (group.total === 0) return null;
          return (
            <div key={kind} className="rounded-lg border divider p-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">{PART_KIND_LABEL[kind]}</span>
                <Badge tone={group.free === 0 ? 'warning' : 'neutral'}>
                  {group.used} / {group.total} belegt
                </Badge>
              </div>
              <ul className="mt-1 space-y-0.5">
                {group.occupied.map((part) => {
                  const marked = remove.some((r) => r.kind === part.kind && r.slot === part.slot);
                  return (
                    <li key={`${part.kind}-${part.slot}`} className="flex items-center justify-between gap-2 text-2xs">
                      <span className={marked ? 'line-through opacity-60' : ''}>
                        <Mono>{part.slot}</Mono> {findPart(part.modelKey)?.name ?? part.modelKey}
                      </span>
                      <button
                        type="button"
                        className="text-panel-500 hover:text-red-600 dark:text-panel-400"
                        onClick={() =>
                          setRemove((prev) =>
                            marked
                              ? prev.filter((r) => !(r.kind === part.kind && r.slot === part.slot))
                              : [...prev, { kind: part.kind, slot: part.slot }],
                          )
                        }
                      >
                        {marked ? 'behalten' : 'ausbauen'}
                      </button>
                    </li>
                  );
                })}
                {group.free > 0 && (
                  <li className="text-2xs text-panel-500 dark:text-panel-400">{group.free} frei</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>

      <h4 className="mb-1 mt-3 text-2xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400">
        Aus dem Lager einbauen
      </h4>
      {Object.keys(stock).length === 0 ? (
        <p className="text-xs text-panel-500 dark:text-panel-400">
          Im Lager {world.datacenters[server.datacenterId]?.name} liegt nichts. Unter „Ersatzteile“ bestellen.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(stock).map(([modelKey, quantity]) => {
            const part = findPart(modelKey);
            const planned = install.filter((k) => k === modelKey).length;
            return (
              <button
                key={modelKey}
                type="button"
                disabled={planned >= quantity}
                className="rounded border divider px-2 py-1 text-2xs hover:bg-panel-100 disabled:opacity-40 dark:hover:bg-panel-800"
                onClick={() => setInstall((prev) => [...prev, modelKey])}
              >
                + {part?.name ?? modelKey}
                <span className="ml-1 text-panel-500">
                  ({planned}/{quantity})
                </span>
              </button>
            );
          })}
        </div>
      )}

      {install.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-2xs text-panel-500 dark:text-panel-400">Geplant:</span>
          {install.map((modelKey, index) => (
            <button
              key={`${modelKey}-${index}`}
              type="button"
              className="rounded bg-accent-600/10 px-2 py-0.5 text-2xs text-accent-700 dark:text-accent-300"
              onClick={() => setInstall((prev) => prev.filter((_, i) => i !== index))}
              title="Aus der Planung nehmen"
            >
              {findPart(modelKey)?.name ?? modelKey} ✕
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div className="mt-3">
          {preview.ok ? (
            <Notice tone="info" title={`Wartungsfenster: ${preview.ticks} h`}>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Field label="Kerne">
                  {preview.before.cpuCores} → {preview.after.cpuCores}
                </Field>
                <Field label="RAM">
                  {bytes(preview.before.ramGb)} → {bytes(preview.after.ramGb)}
                </Field>
                <Field label="SSD">
                  {bytes(preview.before.ssdGb)} → {bytes(preview.after.ssdGb)}
                </Field>
                <Field label="Leistung">
                  {watt(preview.before.maxWatt)} → {watt(preview.after.maxWatt)}
                </Field>
              </div>
              {preview.affectedContractIds.length > 0 && (
                <p className="mt-2 text-xs">
                  {preview.affectedContractIds.length} Verträge sind während des Fensters offline.
                </p>
              )}
            </Notice>
          ) : (
            <Notice tone="warning" title="So nicht möglich">
              {preview.error}
            </Notice>
          )}
        </div>
      )}

      {(server.contractIds.length > 0 || server.vpsIds.length > 0) && (
        <label className="mt-2 flex items-center gap-2 text-xs">
          <input type="checkbox" checked={acceptDowntime} onChange={(e) => setAcceptDowntime(e.target.checked)} />
          Ausfallzeit für die {server.contractIds.length + server.vpsIds.length} laufenden Dienste in Kauf nehmen
        </label>
      )}

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={!preview?.ok}
          onClick={async () => {
            const result = await run({
              type: 'server/planUpgrade',
              serverId: server.id,
              install: install.map((modelKey) => ({ modelKey })),
              remove,
              acceptDowntime,
            });
            if (result.ok) {
              setInstall([]);
              setRemove([]);
              setAcceptDowntime(false);
            }
          }}
        >
          Wartungsfenster starten
        </Button>
        {(install.length > 0 || remove.length > 0) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setInstall([]);
              setRemove([]);
            }}
          >
            Planung verwerfen
          </Button>
        )}
      </div>
    </div>
  );
}

/** Kaufdialog für alle Gerätearten. */
export function BuyHardwareDialog({
  open,
  onClose,
  kind,
  catalog,
  onBuy,
}: {
  open: boolean;
  onClose: () => void;
  kind: 'server' | 'router' | 'switch' | 'firewall' | 'loadbalancer' | 'ups';
  catalog: Awaited<ReturnType<typeof api.catalog>> | null;
  onBuy: (rackId: string, modelKey: string, role?: string) => Promise<boolean>;
}) {
  const world = useGame((s) => s.world);
  const [rackId, setRackId] = useState('');
  const [role, setRole] = useState<ServerRole>('shared-hosting');

  const racks = world ? Object.values(world.racks) : [];
  useEffect(() => {
    if (!rackId && racks.length > 0) setRackId(racks[0].id);
  }, [racks, rackId]);

  const models = (catalog?.[
    kind === 'switch' ? 'switches' : kind === 'ups' ? 'ups' : kind === 'loadbalancer' ? 'loadbalancers' : (`${kind}s` as 'servers')
  ] ?? []) as unknown as {
    key: string;
    name: string;
    price: number;
    description: string;
    heightUnits: number;
    maxWatt: number;
    vendorTier: string;
  }[];

  return (
    <Modal open={open} onClose={onClose} title="Hardware beschaffen" wide>
      <div className="space-y-3">
        {racks.length === 0 ? (
          <Notice tone="warning">Es ist noch kein Rack vorhanden. Zuerst unter „Rechenzentren“ ein Rack anmieten.</Notice>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Zielrack" value={rackId} onChange={(e) => setRackId(e.target.value)}>
              {racks.map((rack) => (
                <option key={rack.id} value={rack.id}>
                  {world?.datacenters[rack.datacenterId]?.city} – {rack.name}
                </option>
              ))}
            </Select>
            {kind === 'server' && (
              <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value as ServerRole)}>
                {Object.entries(ROLE_LABEL)
                  .filter(([value]) => value !== 'unassigned')
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            )}
          </div>
        )}

        {models.length === 0 && <EmptyState title="Keine Modelle verfügbar" description="Weitere Modelle werden durch Forschung freigeschaltet." />}

        {models.map((model) => (
          <div key={model.key} className="surface-muted flex flex-wrap items-center gap-3 p-3">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-medium">
                {model.name}
                <Badge tone={model.vendorTier === 'enterprise' ? 'info' : model.vendorTier === 'standard' ? 'neutral' : 'muted'}>
                  {model.vendorTier}
                </Badge>
              </p>
              <p className="text-xs text-panel-500 dark:text-panel-400">{model.description}</p>
              <p className="mt-0.5 text-2xs text-panel-400">
                {model.heightUnits} HE · bis {watt(model.maxWatt)}
              </p>
            </div>
            <p className="tabular shrink-0 text-sm font-medium">{money(model.price)}</p>
            <Button
              size="sm"
              variant="primary"
              disabled={racks.length === 0}
              onClick={async () => {
                if (await onBuy(rackId, model.key, role)) onClose();
              }}
            >
              Kaufen
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
